import {
  AppSessionId,
  CellId,
  DatetimeValue,
  DisplayTableColumnId,
  DisplayTableConfigId,
  DisplayTableSessionStateId,
  FileUpload,
  Filter,
  FilterCellId,
  FilterGroup,
  FilterType,
  GridElement,
  GridElementType,
  HexVersionId,
  JsonSerializedParameterValueContents,
  MULTISELECT_SELECT_ALL_OPTION,
  ParameterId,
  ParameterInputType,
  ParameterValueContents,
  RelativeDatetimeOffsetValue,
  SavedViewParamType,
  SharedFilterId,
  StringArray,
  TableFiltersDefinition,
  displayTableFilterToFilterCellFilter,
  getFieldAggregationLabel,
  stableEmptyArray,
  stableEmptyObject,
  typedObjectEntries,
  typedObjectKeys,
  typedObjectValues,
} from "@hex/common";
import { isEqual, memoize } from "lodash";
import microMemoize from "micro-memoize";

import { getDisplayTableConfigFromCellContents } from "../../hex-version-multiplayer/handlers/displayTableConfigClientHandlers.js";
import { InputCellMpFragment } from "../../hex-version-multiplayer/HexVersionMPModel.generated.js";
import {
  FilterCellSessionStateMP,
  ParameterValueMP,
  appSessionMPSelectors,
} from "../../redux/slices/appSessionMPSlice.js";
import { hexVersionMPSelectors } from "../../redux/slices/hexVersionMPSlice.js";
import { savedViewDefaultAppSessionSelectors } from "../../redux/slices/savedViewDefaultAppSessionSlice.js";
import { RootState } from "../../redux/store.js";
import {
  createSelector,
  createShallowInputsAndResultSelector,
} from "../../redux/utils/index.js";
import { humanStringFromDate } from "../../util/date.js";
import { safeParseJsonSerializedParameterValueContents } from "../../util/safeParseJsonSerializedParameterValueContents.js";
import {
  getSqlFromFilter,
  getSqlPreviewFromFilterGroup,
  getTextFromElement,
} from "../cell/renderers/filter/generateSqlPreview.js";
import { humanReadableFilterType } from "../cell/renderers/filter/utils.js";
import { relativeDatetimeOffsetToDisplayString } from "../common/RelativeDatePicker.js";

import { DisplayTableFilterSessionStateMpFragment } from "./queries.generated.js";
import {
  SharedFilterStateIdWithStaticCellIdUniqueKey,
  getManuallyLinkedSharedFilterSelectors,
} from "./shared-filter-selectors/useGetManuallyLinkedSFSelectors.js";
import { getPersistedSharedFilterSelectors } from "./shared-filter-selectors/useGetPersistedSFSelectors.js";

/**
 * A representation of inputs that have been modified in a saved view's default app session
 * or the current appSession, for displaying in the SavedViewChangesDropdown popover
 */
export type HumanReadableInputUpdate = {
  /**
   * A unique identifier is required for the react component
   * When CellId is used, the type is a Filter Cell, Parameter, or Display Table Session State
   * When SharedFilterId is used, its a unique key
   * For manual linked filters, since it can apply to multiple cells, we make the key unique by appending the staticCellId.
   */
  id: CellId | SharedFilterId | SharedFilterStateIdWithStaticCellIdUniqueKey;
  label: string;
  /**
   * The human readable value of the input in the current app session
   */
  humanReadableCurrentValue: string | null;
  /**
   * The human readable value of the input in the default app session of the Saved View
   */
  humanReadableDefaultValue?: string | null;
  /**
   * If the input value has been changed in the current app session
   * Will be false for inputs that have been modified in the saved view's default app session
   * but not in the current app session
   */
  isChangedInCurrentSession: boolean;
  /**
   * Tooltip to display in the popover for human readable values that will be truncated
   */
  tooltip?: string;
  paramType: SavedViewParamType;
};

type ParameterValue = {
  valueString: string;
  safeParameterValueContents: ParameterValueContents;
};

type CurrentAndDefaultParameterValue = {
  currentValue: ParameterValue;
  defaultValue: ParameterValue | null;
};

type ParameterCellInputUpdate = {
  label: string;
  cellId: CellId;
  inputType: ParameterInputType;
  isChangedInCurrentSession: boolean;
  currentAndDefaultParameterValue: CurrentAndDefaultParameterValue;
};

type FilterState = {
  filters: FilterGroup | null;
  filterType: FilterType | null;
};

type CurrentAndDefaultFilterCellState = {
  currentFilterState: FilterState;
  defaultFilterState: FilterState;
};

type FilterCellInputUpdate = {
  label: string;
  cellId: CellId;
  currentAndDefaultFilterCellState: CurrentAndDefaultFilterCellState;
  isChangedInCurrentSession: boolean;
};

export type DisplayTableFilterStates = {
  displayTableSessionStateId: DisplayTableSessionStateId;
  additionalFilters: TableFiltersDefinition | null;
};

type DisplayTableFilterStateUpdate = {
  cellId: CellId;
  label: string;
  currentTableFilters: TableFiltersDefinition | null;
  defaultTableFilters: TableFiltersDefinition | null;
  isChangedInCurrentSession: boolean;
};

/**
 * Selector that determines whether or not we should evaluate the current app session against its default app session to determine if saved views
 * are in use or if there are any local changes. This ensures that we do not check the saved view states in previews or states that we don't need them.
 */
export const selectSavedViewsEnabled = (
  state: RootState,
  hexVersionId: HexVersionId,
  appSessionId: AppSessionId,
): boolean => {
  const hexVersionSelectors =
    hexVersionMPSelectors.getHexVersionSelectors(hexVersionId);
  const hexVersion = hexVersionSelectors.select(state);
  const currentVersionIsPreview = hexVersion.preview;

  const appSessionSelectors =
    appSessionMPSelectors.getAppSessionSelectors(appSessionId);
  const appSession = appSessionSelectors.select(state);
  const isViewingScheduledRun = appSession.scheduled;
  const isViewingSnapshot = appSession.snapshot;
  const triggeredViaApi = appSession.triggeredViaApi;
  const savedViewOnAppSession = appSession.savedView;

  // If this is for a scheduled, snapshot, or api run, make sure that
  // it was triggered for a specific savedView so that we can show a read-only button popover.
  const isHistoricalRun =
    isViewingScheduledRun || isViewingSnapshot || triggeredViaApi;

  const canShowSavedViewForHexVersion =
    !currentVersionIsPreview && !hexVersion.canEdit;
  const canShowSavedViewForAppSession =
    !isHistoricalRun || savedViewOnAppSession != null;

  return canShowSavedViewForHexVersion && canShowSavedViewForAppSession;
};

/**
 * Helper method to get a cache key for a selector.
 *
 * Warning: This memoization key is dependent on the appSessionId AND hexVersionId.
 * By default, Lodash will use only the first argument to determine the cache key.
 */
export const getCacheKeyForSelector = (
  hexVersionId: HexVersionId,
  appSessionId: AppSessionId,
): string => `${hexVersionId}-${appSessionId}`;

/**
 * Helper method to get a human readable display value for a parameter change.
 * Handles special cases like datetime ranges, single datetimes, and multiselect values.
 * isDefaultValue is used to determine if the value returned should be the default value for the view or the current value.
 */

const getParameterDisplayValue = (
  param: ParameterCellInputUpdate,
  isDefaultValue?: boolean,
): string | null => {
  const paramValueContents = isDefaultValue
    ? param.currentAndDefaultParameterValue.defaultValue
        ?.safeParameterValueContents
    : param.currentAndDefaultParameterValue.currentValue
        .safeParameterValueContents;
  const valueString = isDefaultValue
    ? param.currentAndDefaultParameterValue.defaultValue?.valueString
    : param.currentAndDefaultParameterValue.currentValue.valueString;
  if (paramValueContents == null || valueString == null) {
    return null;
  }
  switch (param.inputType) {
    case ParameterInputType.DATE:
      if (RelativeDatetimeOffsetValue.guard(paramValueContents)) {
        return relativeDatetimeOffsetToDisplayString(paramValueContents);
      } else if (
        Array.isArray(paramValueContents) &&
        paramValueContents.length === 2
      ) {
        if (
          RelativeDatetimeOffsetValue.guard(paramValueContents[0]) &&
          RelativeDatetimeOffsetValue.guard(paramValueContents[1])
        ) {
          return `${relativeDatetimeOffsetToDisplayString(paramValueContents[0])} to ${relativeDatetimeOffsetToDisplayString(paramValueContents[1])}`;
        } else if (
          DatetimeValue.guard(paramValueContents[0]) &&
          DatetimeValue.guard(paramValueContents[1])
        ) {
          return `${humanStringFromDate(paramValueContents[0], "dynamic")} to ${humanStringFromDate(paramValueContents[1], "dynamic")}`;
        }
      } else if (DatetimeValue.guard(paramValueContents)) {
        return humanStringFromDate(paramValueContents, "dynamic");
      }
      break;
    case ParameterInputType.MULTISELECT:
      if (Array.isArray(paramValueContents)) {
        if (
          StringArray.guard(paramValueContents) &&
          paramValueContents.includes(MULTISELECT_SELECT_ALL_OPTION)
        ) {
          return "All";
        } else {
          return paramValueContents.join(", ");
        }
      }
      break;
    case ParameterInputType.TABLE:
      return "Table contents updated";
    case ParameterInputType.FILE_UPLOAD:
      if (FileUpload.guard(paramValueContents)) {
        return paramValueContents.name;
      }
      break;
  }
  // All other output types can be just the string with opening and closing quotes removed
  return valueString.replace(/^"|"$/g, "") ?? null;
};
/**
 * Helper method to filter out cells that have been changed in a saved view's default sesison
 * but not the current session
 */
const getAllChangedCellIds = (
  cellInputUpdate:
    | ParameterCellInputUpdate[]
    | FilterCellInputUpdate[]
    | DisplayTableFilterStateUpdate[],
): CellId[] => {
  return cellInputUpdate
    .filter(({ isChangedInCurrentSession }) => isChangedInCurrentSession)
    .map(({ cellId }) => cellId);
};

/**
 * Helper method to get a human readable display value for a filter cell change.
 * Returns a simple string representation of the filter values.
 */
const getFilterCellDisplayValue = (filterState: FilterState): string => {
  const filterGroups = filterState.filters;
  const filterType = filterState.filterType;
  if (filterGroups == null || filterGroups.filters.length === 0) {
    if (filterType != null) {
      return humanReadableFilterType(filterType);
    }
    return "No filters applied";
  }

  const element = getSqlPreviewFromFilterGroup({
    group: filterGroups,
    inPreview: true,
    isTableDisplayFilter: false,
    keepRemove: filterType ?? undefined,
    position: [0],
    topLevel: true,
  });
  return getTextFromElement(element);
};

/**
 * Helper method to get a human readable display value for a display table session state change.
 */
const getTableFilterDisplayValue = (
  tableFilters: TableFiltersDefinition | null,
  // The string to output if the filters are empty or null
  defaultStringIfNull: string | null = null,
): string | null => {
  if (tableFilters == null || tableFilters.length === 0) {
    return defaultStringIfNull;
  }

  const filtersToText = tableFilters.map((filter) => {
    const columnNameResolvedFilter = {
      ...filter,
      column: DisplayTableColumnId.check(filter.column) ?? filter.column,
    };
    const converted = displayTableFilterToFilterCellFilter(
      columnNameResolvedFilter,
    );

    // Get aggregation label if present
    const aggregationLabel = filter.aggregation
      ? getFieldAggregationLabel(filter.aggregation)
      : undefined;

    const element = Filter.guard(converted)
      ? getSqlFromFilter({
          filter: converted,
          position: [0],
          aggregation: aggregationLabel,
        })
      : getSqlPreviewFromFilterGroup({
          group: converted,
          inPreview: true,
          isTableDisplayFilter: true,
          position: [0],
          topLevel: true,
          aggregation: aggregationLabel,
        });
    return getTextFromElement(element);
  });
  return filtersToText.join(", ");
};

const getDisplayTableSessionStateChangeSelectors = memoize(
  (hexVersionId: HexVersionId, appSessionId: AppSessionId) => {
    const selectSavedViewsEnabledInternal = (state: RootState): boolean =>
      selectSavedViewsEnabled(state, hexVersionId, appSessionId);

    const makeDisplayTableFilterStateUpdate = microMemoize(
      ({
        cellId,
        currentTableFilters,
        defaultTableFilters,
        isChangedInCurrentSession,
        label,
      }: {
        cellId: CellId;
        currentTableFilters: TableFiltersDefinition | null;
        defaultTableFilters: TableFiltersDefinition | null;
        isChangedInCurrentSession: boolean;
        label: string;
      }): DisplayTableFilterStateUpdate => ({
        cellId,
        label,
        currentTableFilters,
        defaultTableFilters,
        isChangedInCurrentSession,
      }),
      {
        maxSize: 50, // We don't expect a need to memoize more than 50 display table filter states per app session.
      },
    );

    const selectSavedViewDefaultDisplayTableFilterStates = createSelector(
      [
        selectSavedViewsEnabledInternal,
        savedViewDefaultAppSessionSelectors.selectDisplayTableFilterStates,
      ],
      (savedViewsEnabled, displayTableFilterStates) => {
        if (!savedViewsEnabled) {
          return stableEmptyObject() as Record<
            DisplayTableConfigId,
            DisplayTableFilterSessionStateMpFragment
          >;
        }
        return displayTableFilterStates.reduce<
          Record<DisplayTableConfigId, DisplayTableFilterSessionStateMpFragment>
        >((acc, filterState) => {
          acc[filterState.displayTableConfigId] = filterState;
          return acc;
        }, {});
      },
    );

    const selectChangedDisplayTableFilterStates = createSelector(
      [
        selectSavedViewsEnabledInternal,
        hexVersionMPSelectors.getCellContentAwareSelectors(hexVersionId)
          .selectCellIdToLabel,
        hexVersionMPSelectors.getCellContentSelectors(hexVersionId)
          .selectEntitiesByCellContentsId,
        hexVersionMPSelectors.getCellSelectors(hexVersionId)
          .selectAllCellIdsToStaticCellIds,
        savedViewDefaultAppSessionSelectors.selectModifiedParams,
        appSessionMPSelectors.getDisplayTableSessionStateSelectors(appSessionId)
          .selectConfigIdToDisplayTableFilters,
        selectSavedViewDefaultDisplayTableFilterStates,
      ],
      (
        savedViewsEnabled,
        labels,
        cellContents,
        cellIdsToStaticIds,
        modifiedParams,
        currentDisplayTableFilterStates,
        savedViewDefaultDisplayTableFilterStates,
      ) => {
        if (!cellContents || !savedViewsEnabled) {
          return stableEmptyArray<DisplayTableFilterStateUpdate>();
        }

        const changedDisplayTableFilterStates: DisplayTableFilterStateUpdate[] =
          [];
        for (const content of typedObjectValues(cellContents)) {
          const displayTableConfigId =
            getDisplayTableConfigFromCellContents(content)?.id;
          if (displayTableConfigId) {
            const currentDisplayTableFilterState =
              currentDisplayTableFilterStates[displayTableConfigId];
            const defaultDisplayTableFilterState =
              savedViewDefaultDisplayTableFilterStates[displayTableConfigId];

            // If the display filter session state has never existed it will be null,
            // so we don't need to compare it. If it exists and is removed, it will be an empty array
            if (currentDisplayTableFilterState?.additionalFilters != null) {
              const sortedCurrentFilters = [
                ...currentDisplayTableFilterState.additionalFilters,
              ].sort((a, b) => a.column.localeCompare(b.column));
              const sortedDefaultFilters = [
                ...(defaultDisplayTableFilterState?.additionalFilters ?? []),
              ].sort((a, b) => a.column.localeCompare(b.column));
              const hasChanged = !isEqual(
                sortedCurrentFilters,
                sortedDefaultFilters,
              );
              if (hasChanged) {
                changedDisplayTableFilterStates.push(
                  makeDisplayTableFilterStateUpdate({
                    cellId: content.cellId,
                    currentTableFilters:
                      currentDisplayTableFilterState.additionalFilters,
                    defaultTableFilters:
                      defaultDisplayTableFilterState?.additionalFilters,
                    isChangedInCurrentSession: true,
                    label: labels[content.cellId] ?? "",
                  }),
                );
                continue; // If the cell was changed, skip the modifiedCells check
              }
            }
            const modifiedCells = modifiedParams?.staticCellIds ?? [];
            const staticCellId =
              cellIdsToStaticIds && cellIdsToStaticIds[content.cellId];
            if (staticCellId && modifiedCells?.includes(staticCellId)) {
              changedDisplayTableFilterStates.push(
                makeDisplayTableFilterStateUpdate({
                  cellId: content.cellId,
                  currentTableFilters: null,
                  defaultTableFilters:
                    defaultDisplayTableFilterState?.additionalFilters,
                  isChangedInCurrentSession: false,
                  label: labels[content.cellId] ?? "",
                }),
              );
            }
          }
        }
        return changedDisplayTableFilterStates;
      },
    );
    return { selectChangedDisplayTableFilterStates };
  },
  (hexVersionId, appSessionId) =>
    getCacheKeyForSelector(hexVersionId, appSessionId),
);

const getFilterCellInputChangeSelectors = memoize(
  (hexVersionId: HexVersionId, appSessionId: AppSessionId) => {
    const selectSavedViewsEnabledInternal = (state: RootState): boolean =>
      selectSavedViewsEnabled(state, hexVersionId, appSessionId);

    const makeFilterState = microMemoize(
      (
        filters: FilterGroup | null,
        filterType: FilterType | null,
      ): FilterState => ({
        filters,
        filterType,
      }),
      {
        maxSize: 100, // larger size since we call this twice for each filter cell
      },
    );
    const makeCurrentAndDefaultFilterCellState = microMemoize(
      (
        currentFilters: FilterGroup | null,
        currentFilterType: FilterType | null,
        defaultFilters: FilterGroup | null,
        defaultFilterType: FilterType | null,
      ): CurrentAndDefaultFilterCellState => ({
        currentFilterState: makeFilterState(currentFilters, currentFilterType),
        defaultFilterState: makeFilterState(defaultFilters, defaultFilterType),
      }),
      {
        maxSize: 50, // We don't expect a need to memoize more than 50 filter cell states per app session.
      },
    );

    // Memoized method to cache the filter cell objects if their filter contents do not change.
    const makeFilterCellInputUpdate = microMemoize(
      ({
        cellId,
        currentFilterType,
        currentFilters,
        defaultFilterType,
        defaultFilters,
        isChangedInCurrentSession,
        label,
      }: {
        cellId: CellId;
        currentFilters: FilterGroup | null;
        currentFilterType: FilterType | null;
        defaultFilters: FilterGroup | null;
        defaultFilterType: FilterType | null;
        isChangedInCurrentSession: boolean;
        label: string;
      }): FilterCellInputUpdate => ({
        cellId,
        currentAndDefaultFilterCellState: makeCurrentAndDefaultFilterCellState(
          currentFilters,
          currentFilterType,
          defaultFilters,
          defaultFilterType,
        ),
        label,
        isChangedInCurrentSession,
      }),
      {
        maxSize: 50, // We don't expect a need to memoize more than 50 filter cell updates per app session.
      },
    );

    const selectSavedViewDefaultFilterCellSessionStates = createSelector(
      [
        selectSavedViewsEnabledInternal,
        savedViewDefaultAppSessionSelectors.selectFilterCellSessionStates,
      ],
      (savedViewsEnabled, filterCellSessionStates) => {
        if (!savedViewsEnabled) {
          return stableEmptyObject() as Record<
            FilterCellId,
            FilterCellSessionStateMP
          >;
        }
        return filterCellSessionStates.reduce<
          Record<FilterCellId, FilterCellSessionStateMP>
        >((acc, filterCellState) => {
          acc[filterCellState.filterCellId] = filterCellState;
          return acc;
        }, {});
      },
    );

    const selectCurrentFilterCellSessionStates = createSelector(
      [
        selectSavedViewsEnabledInternal,
        appSessionMPSelectors.getFilterCellSessionStateSelectors(appSessionId)
          .selectEntities,
      ],
      (savedViewsEnabled, filterCellSessionStates) => {
        const map: Record<FilterCellId, FilterCellSessionStateMP> = {};
        if (!savedViewsEnabled) {
          return stableEmptyObject() as Record<
            FilterCellId,
            FilterCellSessionStateMP
          >;
        }
        if (filterCellSessionStates) {
          for (const key of typedObjectKeys(filterCellSessionStates)) {
            const filterCellSessionState = filterCellSessionStates[key];
            if (
              filterCellSessionState != null &&
              filterCellSessionState.deletedDate == null
            ) {
              map[filterCellSessionState.filterCellId] = filterCellSessionState;
            }
          }
        }
        return map;
      },
    );

    const selectChangedFilterCells = createSelector(
      [
        selectSavedViewsEnabledInternal,
        hexVersionMPSelectors.getCellContentAwareSelectors(hexVersionId)
          .selectCellIdToLabel,
        hexVersionMPSelectors.getCellContentSelectors(hexVersionId)
          .selectEntitiesByCellContentsId,
        hexVersionMPSelectors.getCellSelectors(hexVersionId)
          .selectAllCellIdsToStaticCellIds,
        savedViewDefaultAppSessionSelectors.selectModifiedParams,
        selectCurrentFilterCellSessionStates,
        selectSavedViewDefaultFilterCellSessionStates,
      ],
      (
        savedViewsEnabled,
        labels,
        cellContents,
        cellIdsToStaticIds,
        modifiedParams,
        currentFilterCellSessionStates,
        savedViewDefaultFilterCellSessionStates,
      ) => {
        if (!cellContents || !savedViewsEnabled) {
          return stableEmptyArray<FilterCellInputUpdate>();
        }

        const changedFilterCells: FilterCellInputUpdate[] = [];
        for (const [_, content] of typedObjectEntries(cellContents)) {
          if (content.__typename === "FilterCell") {
            const currentFilterCellSessionState =
              currentFilterCellSessionStates[content.filterCellId];
            const defaultFilterCellSessionState =
              savedViewDefaultFilterCellSessionStates[content.filterCellId];
            if (currentFilterCellSessionState) {
              // If the saved view has a filter or filterType override, we can use it for comparison.
              // Otherwise, fall back to the hexVersion's default filter and filterType.
              const defaultFilters =
                defaultFilterCellSessionState?.filtersOverride
                  ? defaultFilterCellSessionState.filtersOverride
                  : content.filters;
              const defaultFilterType =
                defaultFilterCellSessionState?.filterTypeOverride
                  ? defaultFilterCellSessionState.filterTypeOverride
                  : content.filterType;
              const filterHasChanged =
                currentFilterCellSessionState.filtersOverride != null &&
                !isEqual(
                  currentFilterCellSessionState.filtersOverride,
                  defaultFilters,
                );
              const filterTypeHasChanged =
                currentFilterCellSessionState.filterTypeOverride != null &&
                !isEqual(
                  currentFilterCellSessionState.filterTypeOverride,
                  defaultFilterType,
                );
              if (filterHasChanged || filterTypeHasChanged) {
                changedFilterCells.push(
                  makeFilterCellInputUpdate({
                    cellId: content.cellId,
                    currentFilters: filterHasChanged
                      ? currentFilterCellSessionState.filtersOverride
                      : defaultFilters,
                    currentFilterType: filterTypeHasChanged
                      ? currentFilterCellSessionState.filterTypeOverride
                      : defaultFilterType,
                    defaultFilters,
                    defaultFilterType,
                    isChangedInCurrentSession: true,
                    label: labels[content.cellId] ?? "",
                  }),
                );
                continue; // If the cell was changed, skip the modifiedCells check
              }

              const modifiedCells = modifiedParams?.staticCellIds ?? [];
              const staticCellId = cellIdsToStaticIds
                ? cellIdsToStaticIds[content.cellId]
                : undefined;
              if (staticCellId && modifiedCells?.includes(staticCellId)) {
                changedFilterCells.push(
                  makeFilterCellInputUpdate({
                    cellId: content.cellId,
                    currentFilters: null,
                    currentFilterType: null,
                    defaultFilters:
                      defaultFilterCellSessionState.filtersOverride,
                    defaultFilterType:
                      defaultFilterCellSessionState.filterTypeOverride,
                    isChangedInCurrentSession: false,
                    label: labels[content.cellId] ?? "",
                  }),
                );
              }
            }
          }
        }
        return changedFilterCells;
      },
    );
    return {
      selectChangedFilterCells,
    };
  },
  (hexVersionId, appSessionId) =>
    getCacheKeyForSelector(hexVersionId, appSessionId),
);

const makeSafeParsedJsonSerializedParameterValueContents = microMemoize(
  (valueString: JsonSerializedParameterValueContents): ParameterValueContents =>
    safeParseJsonSerializedParameterValueContents(valueString),
  {
    // larger max size since we compare the default to this value (for each param, we need 2 values in this map)
    maxSize: 100,
  },
);

const makeParameterValueAndDefaultParameterValue = microMemoize(
  (
    valueString: JsonSerializedParameterValueContents,
    defaultValueString: JsonSerializedParameterValueContents | null,
  ): CurrentAndDefaultParameterValue => {
    return {
      currentValue: {
        valueString,
        safeParameterValueContents:
          safeParseJsonSerializedParameterValueContents(valueString),
      },
      defaultValue:
        defaultValueString != null
          ? {
              valueString: defaultValueString,
              safeParameterValueContents:
                safeParseJsonSerializedParameterValueContents(
                  defaultValueString,
                ),
            }
          : null,
    };
  },
  {
    maxSize: 100,
  },
);

const isParamsEqual = microMemoize(
  (param1: ParameterValueContents, param2: ParameterValueContents): boolean => {
    return isEqual(param1, param2);
  },
  {
    maxSize: 50,
  },
);

/**
 * A memoized selector that will return a mapping of all of the changed input cells that we need to know for
 * Saved Views.
 */
const getParameterChangeSelectors = memoize(
  (hexVersionId: HexVersionId, appSessionId: AppSessionId) => {
    const selectSavedViewsEnabledInternal = (state: RootState): boolean =>
      selectSavedViewsEnabled(state, hexVersionId, appSessionId);

    // Memoized method to cache the parameter cell objects if their params do not change.
    const makeParameterCellUpdate = microMemoize(
      ({
        currentValue,
        defaultValue,
        input,
        isChangedInCurrentSession,
        label,
      }: {
        currentValue: JsonSerializedParameterValueContents;
        label: string;
        defaultValue: JsonSerializedParameterValueContents;
        input: InputCellMpFragment & { cellId: CellId };
        isChangedInCurrentSession: boolean;
      }): ParameterCellInputUpdate => ({
        ...input,
        label,
        isChangedInCurrentSession,
        currentAndDefaultParameterValue:
          makeParameterValueAndDefaultParameterValue(
            currentValue,
            defaultValue,
          ),
      }),
      {
        maxSize: 50, // We don't expect a need to memoize more than 50 parameter cell updates per app session.
      },
    );

    const selectSavedViewDefaultParamValues = createSelector(
      [
        selectSavedViewsEnabledInternal,
        savedViewDefaultAppSessionSelectors.selectParameterValues,
      ],
      (savedViewsEnabled, paramValues) => {
        if (!savedViewsEnabled) {
          return stableEmptyObject() as Record<ParameterId, ParameterValueMP>;
        }
        return paramValues.reduce<Record<ParameterId, ParameterValueMP>>(
          (acc, paramValue) => {
            acc[paramValue.parameterId] = paramValue;
            return acc;
          },
          {},
        );
      },
    );

    const selectCurrentParamValues = createSelector(
      [
        selectSavedViewsEnabledInternal,
        appSessionMPSelectors.getParameterValueSelectors(appSessionId)
          .selectEntities,
      ],
      (savedViewsEnabled, parameterValues) => {
        const map: Record<ParameterId, ParameterValueMP> = {};
        if (!savedViewsEnabled) {
          return stableEmptyObject() as Record<ParameterId, ParameterValueMP>;
        }
        if (parameterValues) {
          for (const key of typedObjectKeys(parameterValues)) {
            const parameterValue = parameterValues[key];
            if (parameterValue != null && parameterValue.deletedDate == null) {
              map[parameterValue.parameterId] = parameterValue;
            }
          }
        }
        return map;
      },
    );

    const selectChangedParameters = createSelector(
      [
        selectSavedViewsEnabledInternal,
        hexVersionMPSelectors.getCellContentAwareSelectors(hexVersionId)
          .selectCellIdToLabel,
        hexVersionMPSelectors.getCellContentSelectors(hexVersionId)
          .selectEntitiesByCellContentsId,
        hexVersionMPSelectors.getCellSelectors(hexVersionId)
          .selectAllCellIdsToStaticCellIds,
        savedViewDefaultAppSessionSelectors.selectModifiedParams,
        selectCurrentParamValues,
        selectSavedViewDefaultParamValues,
      ],
      (
        savedViewsEnabled,
        labels,
        cellContents,
        cellIdsToStaticIds,
        modifiedParams,
        parameterValueMap,
        savedViewDefaultParamValues,
      ) => {
        if (!cellContents || !savedViewsEnabled) {
          return stableEmptyArray<ParameterCellInputUpdate>();
        }

        const modifiedCells = modifiedParams?.staticCellIds ?? [];

        const changedParameters: ParameterCellInputUpdate[] = [];
        for (const content of typedObjectValues(cellContents)) {
          if (content.__typename === "Parameter") {
            const defaultSavedViewParamValue =
              savedViewDefaultParamValues[content.inputCellId];
            const parameterValue = parameterValueMap[content.inputCellId];
            const staticCellId = cellIdsToStaticIds
              ? cellIdsToStaticIds[content.cellId]
              : undefined;

            if (parameterValue) {
              const structuredParamValueContents =
                makeSafeParsedJsonSerializedParameterValueContents(
                  parameterValue.valueString,
                );

              let defaultValueString = null;
              if (defaultSavedViewParamValue != null) {
                const structuredParam2 =
                  makeSafeParsedJsonSerializedParameterValueContents(
                    defaultSavedViewParamValue.valueString,
                  );
                defaultValueString = isParamsEqual(
                  structuredParamValueContents,
                  structuredParam2,
                )
                  ? null
                  : defaultSavedViewParamValue.valueString;
              } else {
                const structuredParam2 =
                  makeSafeParsedJsonSerializedParameterValueContents(
                    content.defaultValueString,
                  );
                defaultValueString = isParamsEqual(
                  structuredParamValueContents,
                  structuredParam2,
                )
                  ? null
                  : content.defaultValueString;
              }

              if (defaultValueString != null) {
                changedParameters.push(
                  makeParameterCellUpdate({
                    input: content,
                    currentValue: parameterValue.valueString,
                    isChangedInCurrentSession: true,
                    label: labels[content.cellId] ?? "",
                    defaultValue: defaultValueString,
                  }),
                );
              } else if (
                staticCellId &&
                modifiedCells?.includes(staticCellId)
              ) {
                changedParameters.push(
                  makeParameterCellUpdate({
                    input: content,
                    currentValue: parameterValue.valueString,
                    isChangedInCurrentSession: false,
                    label: labels[content.cellId] ?? "",
                    defaultValue: defaultSavedViewParamValue.valueString,
                  }),
                );
              }
            }
          }
        }
        return changedParameters;
      },
    );

    return {
      selectChangedParameters,
    };
  },
  (hexVersionId, appSessionId) =>
    getCacheKeyForSelector(hexVersionId, appSessionId),
);

/**
 * The aggregated set of input changes that we should detect for Saved Views. This will include
 * parameter changes and filter cell changes.
 */
export const getViewSelectors = memoize(
  (hexVersionId: HexVersionId, appSessionId: AppSessionId) => {
    const { selectChangedParameters } = getParameterChangeSelectors(
      hexVersionId,
      appSessionId,
    );

    const { selectChangedFilterCells } = getFilterCellInputChangeSelectors(
      hexVersionId,
      appSessionId,
    );

    const {
      selectChangedPersistedSFHumanReadableDiffValues,
      selectChangedSharedFilterIds,
    } = getPersistedSharedFilterSelectors(hexVersionId, appSessionId);

    const {
      selectChangedCellIdsForManuallyLinkedFilters,
      selectChangedManuallyLinkedHumanReadableDiffValues,
    } = getManuallyLinkedSharedFilterSelectors(hexVersionId, appSessionId);

    const { selectChangedDisplayTableFilterStates } =
      getDisplayTableSessionStateChangeSelectors(hexVersionId, appSessionId);

    // Memoized method to compute human readable output for parameter cells
    const getHumanReadableParameterCellUpdate = microMemoize(
      (param: ParameterCellInputUpdate): HumanReadableInputUpdate => ({
        id: param.cellId,
        label: param.label,
        isChangedInCurrentSession: param.isChangedInCurrentSession,
        humanReadableCurrentValue: getParameterDisplayValue(param),
        humanReadableDefaultValue: getParameterDisplayValue(param, true),
        paramType: SavedViewParamType.INPUT,
      }),
      {
        maxSize: 50,
      },
    );

    // Memoized method to compute human readable output for filter cells
    const getHumanReadableFilterCellUpdate = microMemoize(
      (filterCell: FilterCellInputUpdate): HumanReadableInputUpdate => ({
        id: filterCell.cellId,
        label: filterCell.label,
        isChangedInCurrentSession: filterCell.isChangedInCurrentSession,
        humanReadableCurrentValue: getFilterCellDisplayValue(
          filterCell.currentAndDefaultFilterCellState.currentFilterState,
        ),
        humanReadableDefaultValue: getFilterCellDisplayValue(
          filterCell.currentAndDefaultFilterCellState.defaultFilterState,
        ),
        paramType: SavedViewParamType.FILTER_CELL,
      }),
      {
        maxSize: 50,
      },
    );

    const getHumanReadableDisplayTableFilterStateUpdate = microMemoize(
      (displayTableFilterState: DisplayTableFilterStateUpdate) => ({
        id: displayTableFilterState.cellId,
        label: displayTableFilterState.label,
        isChangedInCurrentSession:
          displayTableFilterState.isChangedInCurrentSession,
        humanReadableCurrentValue: getTableFilterDisplayValue(
          displayTableFilterState.currentTableFilters,
          "No filters applied",
        ),
        humanReadableDefaultValue: getTableFilterDisplayValue(
          displayTableFilterState.defaultTableFilters,
        ),
        paramType: SavedViewParamType.DISPLAY_TABLE,
      }),
      {
        maxSize: 50,
      },
    );

    const selectAllHumanReadableDiffValues: (
      state: RootState,
    ) => HumanReadableInputUpdate[] = createShallowInputsAndResultSelector(
      [
        selectChangedParameters,
        selectChangedFilterCells,
        selectChangedDisplayTableFilterStates,
        selectChangedManuallyLinkedHumanReadableDiffValues,
        selectChangedPersistedSFHumanReadableDiffValues,
      ],
      (
        changedParameters,
        changedFilterCells,
        changedDisplayTableSessionStates,
        changedManualFilters,
        changedPersistedSharedFilters,
      ) => {
        return [
          ...changedParameters.reduce<Array<HumanReadableInputUpdate>>(
            (acc, param) => [
              ...acc,
              getHumanReadableParameterCellUpdate(param),
            ],
            [],
          ),
          ...changedFilterCells.reduce<Array<HumanReadableInputUpdate>>(
            (acc, filterCell) => [
              ...acc,
              getHumanReadableFilterCellUpdate(filterCell),
            ],
            [],
          ),
          ...changedDisplayTableSessionStates.reduce<
            Array<HumanReadableInputUpdate>
          >(
            (acc, displayTableFilterState) => [
              ...acc,
              getHumanReadableDisplayTableFilterStateUpdate(
                displayTableFilterState,
              ),
            ],
            [],
          ),
          ...changedManualFilters.reduce<Array<HumanReadableInputUpdate>>(
            (acc, manualFilter) => [...acc, manualFilter],
            [],
          ),
          ...changedPersistedSharedFilters.reduce<
            Array<HumanReadableInputUpdate>
          >(
            (acc, persistedFilter) => [
              ...acc,
              persistedFilter.humanReadableInputUpdate,
            ],
            [],
          ),
        ];
      },
    );
    const selectChangesCount: (state: RootState) => number = createSelector(
      [selectAllHumanReadableDiffValues],
      (allDiffValues) => {
        const changes = allDiffValues.filter(
          ({ isChangedInCurrentSession }) => isChangedInCurrentSession,
        );
        return Object.keys(changes).length;
      },
    );

    const allChangedCellIds = createSelector(
      [
        selectChangedParameters,
        selectChangedFilterCells,
        selectChangedDisplayTableFilterStates,
        selectChangedCellIdsForManuallyLinkedFilters,
      ],
      (
        changedParameters,
        changedFilterCells,
        changedDisplayTableFilterStates,
        changedManuallyLinkedFilters,
      ) => {
        return new Set([
          ...getAllChangedCellIds([...changedParameters]),
          ...getAllChangedCellIds([...changedFilterCells]),
          ...getAllChangedCellIds([...changedDisplayTableFilterStates]),
          ...changedManuallyLinkedFilters,
        ]);
      },
    );

    /**
     * This selector will be used for Grid Elements to determine whether or not they should be styled.
     */
    const selectShowGridElementChangedFromDefaultAppSession: (
      state: RootState,
    ) => (el: GridElement) => boolean = createSelector(
      [allChangedCellIds, selectChangedSharedFilterIds],
      (changedCellIds, changedFilterIds) =>
        ({ entityId, type }: GridElement) => {
          switch (type) {
            case GridElementType.CELL:
              return changedCellIds.has(entityId);
            case GridElementType.SHARED_FILTER:
              return changedFilterIds.has(entityId);
            default:
              return false;
          }
        },
    );

    return {
      selectSavedViewsEnabled,
      selectChangesCount,
      selectShowGridElementChangedFromDefaultAppSession,
      selectAllHumanReadableDiffValues,
    };
  },
  (hexVersionId, appSessionId) =>
    getCacheKeyForSelector(hexVersionId, appSessionId),
);
